import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { priceToDigits, toFixedSafe, clamp } from "../helpers";
import fuelAnimationGif from "../assets/images/refueling.gif";
import Text from "../../../components/ui/Text";
import loadingIcon from "../assets/images/loading.png";
import { useAudioEffects } from "../../../hooks/use-audio-effects";
import fuelSound from "../../../assets/audio/fuel.wav";
import clickSound from "../../../assets/audio/click.wav";

type DigitCardProps = {
  children: React.ReactNode;
  className?: string;
};

const DigitCard: React.FC<DigitCardProps> = ({ children, className }) => {
  return (
    <div
      className={
        "w-[80px] h-[90px] 1520px:h-[130px] 1520px:w-[140px] select-none !font-[300] rounded-md border-[1px] border-[#56F5FF]/25 bg-[#56F5FF]/5 " +
        "backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] flex items-center justify-center " +
        "text-3xl md:text-4xl font-semibold text-slate-100 tracking-widest" +
        (className ? ` ${className}` : "")
      }
    >
      {children}
    </div>
  );
};

// Stack showing the current price as "$ 2 5 2 2" style

type PriceDisplayProps = {
  amount: number;
  currency?: string; // default: "$"
  decimals?: number; // default: 2
  minDigits?: number; // minimum number of digit cards after the currency
  showDecimalDot?: boolean; // if true, render a subtle dot separator before cents
  className?: string;
};

const PriceDisplay: React.FC<PriceDisplayProps> = ({
  amount,
  currency = "$",
  decimals = 2,
  minDigits = 4,
  showDecimalDot = false,
  className,
}) => {
  const digits = useMemo(
    () => priceToDigits(amount, { decimals, minDigits }),
    [amount, decimals, minDigits]
  );
  const dotIndex = digits.length - decimals; // index where the dot would appear

  return (
    <div className={"flex items-center gap-3  " + (className ?? "")}>
      <DigitCard className="flex-1">{currency}</DigitCard>
      {digits.map((d, i) => (
        <DigitCard className="flex-[1] relative">
          {d}
          {showDecimalDot && i === dotIndex - 1 && (
            <span className="pointer-events-none absolute right-[-20%] translate-x-[-100%] bottom-1 text-lg p-[5px] rounded-full bg-slate-300/80"></span>
          )}
        </DigitCard>
      ))}
    </div>
  );
};

// Rounded square icon button

type SquareButtonProps = {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  intent?: "default" | "positive" | "danger";
  className?: string;
};

const SquareButton: React.FC<SquareButtonProps> = ({
  label,
  onClick,
  disabled,
  intent = "default",
  className,
}) => {
  const { playEffect } = useAudioEffects();
  const base =
    "h-[42px] w-[42px] flex items-center justify-center grid place-items-center rounded-md border text-lg font-semibold transition " +
    "disabled:opacity-40 disabled:cursor-not-allowed";
  const palette: Record<string, string> = {
    default:
      "bg-white/5 border-white/10 text-slate-100 hover:bg-white/10 active:scale-95",
    positive:
      "bg-[#56F5FF]/5 border-[#56F5FF]/25 active:scale-95 text-[#56F5FF] hover:bg-[#56F5FF]/20",
    danger:
      "bg-[#FF4141]/5 border-[#FF4141]/25 active:scale-95 text-[#FF4141] hover:bg-[#FF4141]/20",
  };
  return (
    <button
      title="Click me"
      type="button"
      className={`${base} ${palette[intent]} ${className ?? ""}`}
      onClick={() => {
        playEffect(clickSound);
        if (onClick) onClick();
      }}
      disabled={disabled}
    >
      <span className="font-[400] text-4xl">{label}</span>
    </button>
  );
};

// Small pill button for MAX/MIN

type PillButtonProps = {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
};

const PillButton: React.FC<PillButtonProps> = ({
  children,
  onClick,
  disabled,
  className = "",
}) => {
  const { playEffect } = useAudioEffects();

  return (
    <button
      type="button"
      onClick={() => {
        if (onClick) onClick();

        playEffect(clickSound);
      }}
      disabled={disabled}
      className={
        "px-5  py-1 rounded-md  text-xs font-[300] tracking-wide transition text-white border-[1px] hover:opacity-80 " +
        className
      }
    >
      {children}
    </button>
  );
};

// =============== Main Widget ===============

type FuelFillerWidgetProps = {
  capacityLiters: number; // e.g. 50
  pricePerLiter: number; // e.g. 1.008
  /** Controlled current liters (if you want to control from parent). */
  value?: number;
  /** Uncontrolled starting liters. */
  defaultValue?: number;
  step?: number; // 1 liter by default
  minLiters?: number; // 0 by default
  currency?: string; // default: "$"
  decimals?: number; // 2 by default (for cents)
  minDigits?: number; // 4 by default (after currency)
  showDecimalDot?: boolean; // false by default (matches screenshot)
  className?: string;
  // callbacks
  onChange?: (liters: number) => void;
  onConfirmFill?: (liters: number, totalPrice: number) => void;
};

const useControllable = (controlled: number | undefined, defaultValue = 0) => {
  const [uncontrolled, setUncontrolled] = useState(defaultValue);
  const isControlled = controlled !== undefined;
  const value = isControlled ? (controlled as number) : uncontrolled;
  const setValue = useCallback(
    (next: number) => {
      if (!isControlled) setUncontrolled(next);
    },
    [isControlled]
  );
  return [value, setValue] as const;
};

const litersLabel = (v: number) => `${v} ${v === 1 ? "Liter" : "Liters"}`;

const FuelFiller: React.FC<FuelFillerWidgetProps> = ({
  capacityLiters,
  pricePerLiter,
  value,
  defaultValue = 0,
  step = 1,
  minLiters = 0,
  currency = "$",
  decimals = 2,
  minDigits = 4,
  showDecimalDot = false,
  className,
  onChange,
  onConfirmFill,
}) => {
  const [liters, setLiters] = useControllable(value, defaultValue);
  const [refueling, setRefueling] = useState<boolean>(false);
  const { playEffect } = useAudioEffects();

  const max = Math.max(minLiters, capacityLiters);
  const min = Math.min(minLiters, capacityLiters);

  const setClamped = useCallback(
    (next: number) => {
      const clamped = clamp(Math.round(next), min, max);
      setLiters(clamped);
      onChange?.(clamped);
    },
    [max, min, onChange, setLiters]
  );

  const inc = useCallback(
    () => setClamped(liters + step),
    [liters, setClamped, step]
  );
  const dec = useCallback(
    () => setClamped(liters - step),
    [liters, setClamped, step]
  );
  const setMax = useCallback(() => setClamped(max), [max, setClamped]);
  const setMin = useCallback(() => setClamped(min), [min, setClamped]);

  const totalPrice = useMemo(
    () => liters * pricePerLiter,
    [liters, pricePerLiter]
  );

  // Accessibility: announce price changes
  const liveRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (liveRef.current)
      liveRef.current.textContent = `${currency}${toFixedSafe(
        totalPrice,
        decimals
      ).toFixed(decimals)}`;
  }, [currency, decimals, totalPrice]);

  const canDec = liters > min;
  const canInc = liters < max;

  return (
    <div className={`bender-font ${className}`}>
      {refueling && (
        <div className="fixed top-0 left-0 w-full h-full bg-black/80 z-[100] flex items-center justify-center flex-col space-y-5">
          <img
            src={fuelAnimationGif}
            alt="Fuel"
            className="w-full h-full max-w-[350px] max-h-[350px] object-contain rounded-full"
          />
          <Text size="xl" className="text-white">
            Refueling in progress
          </Text>

          <div className="animate-spin">
            {" "}
            <img src={loadingIcon} alt="Fuel" className="w-[30px] h-[30px]" />
          </div>
        </div>
      )}

      {/* Price Row */}
      <PriceDisplay
        amount={totalPrice}
        currency={currency}
        decimals={decimals}
        minDigits={minDigits}
        showDecimalDot={showDecimalDot}
        className="mb-5 md:mb-3"
      />

      {/* Controls Row */}

      <div className="flex items-center justify-between gap-3 rounded-md bg-[radial-gradient(circle,rgba(255,255,255,0.06)_0%,rgba(255,255,255,0.01)_100%)] py-2 px-4 border-[1px] border-r-white/10 border-l-white/10 border-t-white/15 border-b-0">
        <SquareButton
          label="–"
          onClick={dec}
          disabled={!canDec}
          intent="danger"
        />

        <div className="flex flex-col items-center justify-center text-slate-200">
          <div className="text-base md:text-lg font-medium">
            {liters} <span className="text-slate-400">/</span>{" "}
            {litersLabel(capacityLiters)}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex flex-col gap-2 mr-1">
            <PillButton
              className="border-[#56F5FF]/25 bg-[#56F5FF]/5"
              onClick={setMax}
              disabled={!canInc}
            >
              MAX
            </PillButton>
            <PillButton
              className="border-[#FF4141]/25 bg-[#FF4141]/5 active:scale-95"
              onClick={setMin}
              disabled={!canDec}
            >
              MIN
            </PillButton>
          </div>
          <SquareButton
            label="+"
            onClick={inc}
            disabled={!canInc}
            intent="positive"
          />
        </div>
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={() => {
          if (refueling) return; // in progress
          playEffect(fuelSound);
          setRefueling(true);

          setTimeout(() => {
            setRefueling(false);
            onConfirmFill?.(liters, toFixedSafe(totalPrice, decimals));
          }, 3000);
        }}
        className={
          "mt-4 h-14 w-full  bg-gradient-to-r rounded-md from-[#298389] via-[#38EAF5] to-[#298389] text-black/70 hover:text-black/90 border-[#38EAF5] border-[1px] " +
          "font-semibold text-lg tracking-wide shadow-lg transition hover:opacity-90 active:scale-[0.99]"
        }
      >
        Fill my Car
      </button>

      {/* Hidden live region for screen readers */}
      <div ref={liveRef} aria-live="polite" className="sr-only" />
    </div>
  );
};

export default FuelFiller;
